/**
 * Light Theme Configuration
 *
 * This file defines the complete Material-UI theme for the application.
 * It extends React Admin's default theme with custom colors, typography,
 * and component styles.
 *
 * CUSTOMIZATION GUIDE:
 * 1. Update `lightPalette` to change brand colors
 * 2. Modify `custom` object for layout and component-specific settings
 * 3. Adjust component overrides in `baseTheme.components` for global styles
 */

import { defaultTheme } from 'react-admin';
import { createTheme, Palette, Theme, PaletteOptions } from '@mui/material/styles';
import { esES } from '@mui/material/locale';

// ============================================
// TYPE DECLARATIONS
// ============================================

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      radius: Record<string, string>;
      shadows: Record<string, string>;
      focusRing: string;
      login: {
        containerGradient: (palette: Palette) => string;
        topBarGradient: (palette: Palette) => string;
        fieldBackground: (palette: Palette) => string;
        logoFilter: string;
      };
      layout: Record<string, any>;
      userMenu: Record<string, any>;
      userAvatar: Record<string, any>;
      menu: Record<string, any>;
    };
  }

  interface ThemeOptions {
    custom?: {
      radius?: Record<string, string>;
      shadows?: Record<string, string>;
      focusRing?: string;
      login?: {
        containerGradient?: (palette: Palette) => string;
        topBarGradient?: (palette: Palette) => string;
        fieldBackground?: (palette: Palette) => string;
        logoFilter?: string;
      };
      layout?: Record<string, any>;
      userMenu?: Record<string, any>;
      userAvatar?: Record<string, any>;
      menu?: Record<string, any>;
    };
  }

  // React-admin component overrides
  interface Components {
    RaList?: { styleOverrides?: Record<string, any> };
    RaTopToolbar?: { styleOverrides?: Record<string, any> };
    RaDatagrid?: { styleOverrides?: Record<string, any> };
  }

}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    /** CTA inside a TopToolbar that creates a new resource. Same fill as
     *  the contained primary, kept as a named variant so consumer apps
     *  can express intent ("create resource") in JSX. */
    createButton: true;
    /** Subtle inline action — ghost button. Equivalent to MUI text
     *  variant but with consistent ink-on-paper hover affordance. */
    ghost: true;
    /** Destructive action — red fill, used for delete confirmations
     *  and similar irreversible operations. */
    danger: true;
  }
}

// ============================================
// DESIGN TOKENS
// ============================================

const radius = {
  small: '4px',
  medium: '6px',
  large: '8px',
  chip: '4px',
  full: '9999px',
};

const custom = {
  radius,
  shadows: {
    none: '0 1px 2px 0 rgb(0 0 0 / 0.01)',
    subtle: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    medium: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    strong: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    login: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
  },
  /** Soft halo shown on :focus-visible across interactive chrome.
   *  Built off the brand primary at low alpha so it works on both light
   *  and dark backgrounds. Applied via MuiCssBaseline below. */
  focusRing: '0 0 0 3px rgba(0, 147, 178, 0.25)',
  login: {
    containerGradient: (palette: Palette) =>
      `linear-gradient(135deg, ${palette.grey[50]} 0%, ${palette.grey[200]} 100%)`,
    topBarGradient: (palette: Palette) =>
      `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.primary.light} 100%)`,
    fieldBackground: (palette: Palette) => palette.grey[50],
    logoFilter: 'drop-shadow(0 4px 6px rgb(0 0 0 / 0.1))',
  },
  layout: {
    desktopContainerPadding: '8px 8px 8px 8px',
    gap: '0px',
    mobileContainerPadding: '4px',
    mobileTopMargin: '16px',
    card: {
      borderRadius: radius.medium,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      padding: { xs: '16px', sm: '24px' },
    },
  },
  userMenu: {
    paper: {
      width: '280px',
      borderRadius: radius.large,
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    },
  },
  userAvatar: {
    size: '40px',
    bg: '#F1F5F9',
    color: '#64748B',
    iconSize: '24px',
  },
  menu: {
    openWidth: '230px',
    closedWidth: '64px',
    item: {
      borderRadius: radius.small,
      margin: '2px 0',
      padding: '10px 12px',
      gap: '12px',
      color: (theme: Theme) => theme.palette.text.primary,
      hover: {
        color: (theme: Theme) => theme.palette.primary.main,
        backgroundColor: (theme: Theme) => theme.palette.action.hover,
      },
      active: {
        color: (theme: Theme) => theme.palette.primary.contrastText,
        backgroundColor: (theme: Theme) => theme.palette.primary.main,
        fontWeight: 600,
        boxShadow: (theme: Theme) => `0 4px 12px -4px ${theme.palette.primary.main}80`,
        hover: {
          backgroundColor: (theme: Theme) => theme.palette.primary.dark,
        },
      },
      closedActiveIcon: {
        backgroundColor: (theme: Theme) => `${theme.palette.primary.main}15`,
        color: (theme: Theme) => theme.palette.primary.main,
        borderRadius: '6px',
      },
    },
  },
};

// ============================================
// COLOR PALETTE
// ============================================

/**
 * Main color palette
 *
 * CUSTOMIZATION: Change these values to match your brand colors
 */
const lightPalette: PaletteOptions = {
  mode: 'light',
  // Primary brand color
  primary: {
    main: '#0093B2',
    light: '#53C3D2',
    dark: '#00697A',
    contrastText: '#FFFFFF',
  },
  // Secondary color
  secondary: {
    main: '#64748B',
    light: '#94A3B8',
    dark: '#475569',
    contrastText: '#FFFFFF',
  },
  // Background colors
  background: {
    default: '#E8EDF2',
    paper: '#FFFFFF',
    appbar: '#E8EDF2',
    menu: '#FFFFFF',
  } as any,
  // Text colors
  text: {
    primary: '#1E293B',
    secondary: '#475569',
  },
  // Status colors
  error: {
    main: '#FB7185',
    light: '#FDA4AF',
    dark: '#9F1239',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FB923C',
    light: '#FDBA74',
    dark: '#9A3412',
    contrastText: '#334155',
  },
  info: {
    main: '#3B82F6',
    light: '#60A5FA',
    dark: '#1E40AF',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#86be9c',
    light: '#e3ffed',
    dark: '#149949',          /* fix invalid 9-digit hex (was '#149949ff') */
    contrastText: '#FFFFFF',
  },
  divider: '#E2E8F0',
  grey: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
};

// ============================================
// THEME CREATION
// ============================================

const baseTheme = createTheme({
  ...defaultTheme,
  palette: lightPalette,
  custom: custom,
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    htmlFontSize: 16,
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', lineHeight: 1.4, color: '#64748B' },
    button: { fontWeight: 500, letterSpacing: '0.02em' },
  },
});

// ============================================
// COMPONENT OVERRIDES
// ============================================

/* The theme is built then re-wrapped with the es-ES locale so MUI's
   built-in strings (pagination, date pickers, tables, a11y labels) ship
   in formal Spanish out of the box. Consumer apps can pass a different
   locale at the createTheme call site if they need en-US, etc.
   The locale is the second arg of the trailing createTheme below. */
export const lightTheme = createTheme({
  ...baseTheme,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '.RaList-main': {
          '& .RaList-actions': {
            borderBottom: `1px solid ${baseTheme.palette.divider}`,
          },
        },
        /* Visible focus halo on keyboard navigation — required for
           AA accessibility ("focus state always visible"). The
           default MUI outline is replaced with a brand-tinted ring
           that works against both paper and chrome surfaces. */
        '*:focus-visible': {
          outline: 'none',
          boxShadow: custom.focusRing,
          borderRadius: radius.small,
        },
        'button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible': {
          outline: 'none',
          boxShadow: custom.focusRing,
        },
      },
    },

    // Button styles
    MuiButton: {
      defaultProps: {
        size: 'small' as const,
      },
      styleOverrides: {
        root: {
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          fontWeight: 500,
          borderRadius: radius.small,
          textTransform: 'none',
          padding: '8px 16px',
          transition: 'background-color 0.3s ease, color 0.3s ease',
        },
      },
      variants: [
        // CTA inside a TopToolbar that creates a new resource. Same fill
        // as MUI contained primary, kept as a named variant so consumer
        // apps can express intent in JSX. Mobile FAB transformation was
        // dropped — react-admin's TopToolbar stays sticky and reachable
        // on small viewports without a floating overlay covering the
        // RaList pagination controls.
        {
          props: { variant: 'createButton' },
          style: {
            backgroundColor: baseTheme.palette.primary.main,
            color: '#FFFFFF',
            boxShadow: 'none',
            padding: '6px 16px',
            fontWeight: 500,
            fontSize: '1rem',
            minWidth: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            '&:hover': {
              backgroundColor: baseTheme.palette.primary.light,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem',
              color: '#FFFFFF',
            },
          },
        },
        // Subtle inline action — text-style ghost. Equivalent to
        // MUI text variant but with consistent ink-on-paper hover.
        {
          props: { variant: 'ghost' },
          style: {
            backgroundColor: 'transparent',
            color: baseTheme.palette.text.primary,
            boxShadow: 'none',
            padding: '6px 12px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: baseTheme.palette.action.hover,
            },
          },
        },
        // Destructive action — red fill, used for delete confirmations
        // and similar irreversible operations. Distinct from
        // validation-level error tinting (salmon, used inline on inputs).
        {
          props: { variant: 'danger' },
          style: {
            backgroundColor: baseTheme.palette.error.dark,
            color: '#FFFFFF',
            boxShadow: 'none',
            padding: '6px 16px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#9F1239',
            },
            '& .MuiSvgIcon-root': {
              fontSize: '1.25rem',
              color: '#FFFFFF',
            },
          },
        },
      ],
    },

    // Icon button styles
    MuiIconButton: {
      styleOverrides: {
        root: {
          border: `1px solid ${baseTheme.palette.primary.dark}20`,
          borderRadius: radius.small,
          color: baseTheme.palette.primary.main,
          width: 36,
          height: 32,
          '&:hover': {
            backgroundColor: baseTheme.palette.primary.dark,
            color: baseTheme.palette.common.white,
          },
        },
        colorInherit: {
          color: baseTheme.palette.text.secondary,
          border: `1px solid ${baseTheme.palette.text.secondary}20`,
          '&:hover': {
            color: baseTheme.palette.primary.main,
            backgroundColor: baseTheme.palette.action.hover,
            border: `1px solid ${baseTheme.palette.primary.main}30`,
          },
        },
        colorPrimary: {
          color: baseTheme.palette.primary.main,
          border: `1px solid ${baseTheme.palette.primary.main}30`,
          '&:hover': { backgroundColor: `${baseTheme.palette.primary.main}14` },
        },
        colorSecondary: {
          color: baseTheme.palette.secondary.main,
          border: `1px solid ${baseTheme.palette.secondary.main}30`,
          '&:hover': { backgroundColor: `${baseTheme.palette.secondary.main}14` },
        },
        colorError: {
          color: baseTheme.palette.error.main,
          border: `1px solid ${baseTheme.palette.error.main}30`,
          '&:hover': { backgroundColor: `${baseTheme.palette.error.main}14` },
        },
        colorWarning: {
          color: baseTheme.palette.warning.main,
          border: `1px solid ${baseTheme.palette.warning.main}30`,
          '&:hover': { backgroundColor: `${baseTheme.palette.warning.main}14` },
        },
        colorInfo: {
          color: baseTheme.palette.info.main,
          border: `1px solid ${baseTheme.palette.info.main}30`,
          '&:hover': { backgroundColor: `${baseTheme.palette.info.main}14` },
        },
        colorSuccess: {
          color: baseTheme.palette.success.main,
          border: `1px solid ${baseTheme.palette.success.main}30`,
          '&:hover': { backgroundColor: `${baseTheme.palette.success.main}14` },
        },
        sizeLarge: { width: 48, height: 48 },
        sizeSmall: { width: 32, height: 32 },
      },
    },

    // Badge
    MuiBadge: {
      defaultProps: {
        color: 'primary',
        max: 99,
        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      },
      styleOverrides: {
        badge: ({ theme }) => ({
          borderRadius: theme.custom?.radius?.chip ?? '4px',
          minWidth: 18,
          height: 18,
          padding: '0 4px',
          border: `2px solid ${theme.palette.common.white}`,
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.common.white,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          lineHeight: 1,
        }),
      },
    },

    // Dialog actions
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px',
          fontWeight: 500,
          minWidth: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          '& .MuiButton-text': {
            backgroundColor: '#f8f9fa',
            color: '#495057',
            border: '1px solid #e9ecef',
            '&:hover': { backgroundColor: '#e9ecef' },
          },
          '& .MuiButton-contained': {
            backgroundColor: baseTheme.palette.primary.main,
            color: '#FFFFFF',
            border: `1px solid ${baseTheme.palette.primary.main}`,
            '&:hover': {
              backgroundColor: baseTheme.palette.primary.dark,
              borderColor: baseTheme.palette.primary.dark,
            },
          },
          '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
        },
      },
    },

    // React Admin List
    RaList: {
      styleOverrides: {
        root: {
          '& .RaList-main': {
            paddingTop: 16,
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },

    // React Admin Top Toolbar
    RaTopToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          marginBottom: baseTheme.spacing(2),
        },
      },
    },

    // React Admin DataGrid
    RaDatagrid: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.background.paper,
          border: `1px solid ${baseTheme.palette.divider}`,
          borderRadius: radius.medium,
          overflow: 'hidden',

          '& .RaDatagrid-headerCell': {
            backgroundColor: `${baseTheme.palette.primary.dark}10`,
            borderBottom: `2px solid ${baseTheme.palette.divider}`,
            fontWeight: 600,
            fontSize: '0.875rem',
            /* uppercase + letter-spacing dropped — sentence-case headers
               are scanned faster (Arditi & Cho 2007: -13-18% reading
               speed under all-caps); the tinted bg + 600 weight
               already separate header from body rows. */
            padding: '12px',
            color: baseTheme.palette.text.primary,
          },

          '& .RaDatagrid-row': {
            backgroundColor: baseTheme.palette.common.white,
            transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s',
            '&:hover': { backgroundColor: baseTheme.palette.action.hover },
          },

          '& .RaDatagrid-rowCell': {
            fontSize: '0.875rem',
            padding: '10px 12px',
            borderBottom: `1px solid ${baseTheme.palette.divider}`,
            color: baseTheme.palette.text.primary,
            '&:last-of-type': { borderRight: 'none' },
          },

          'tbody tr:last-child .RaDatagrid-rowCell': {
            borderBottom: 'none',
          },

          '& .MuiTablePagination-root': {
            borderTop: `1px solid ${baseTheme.palette.divider}`,
            backgroundColor: baseTheme.palette.grey[50],
            padding: baseTheme.spacing(1),
          },

          '& .RaDatagrid-rowCell:has(button)': {
            paddingTop: '6px',
            paddingBottom: '6px',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          },

          '& button.RaButton-link': {
            fontSize: '0.8rem',
            color: baseTheme.palette.text.secondary,
            padding: '4px 8px',
            borderRadius: radius.small,
            transition: 'color 0.2s ease, background-color 0.2s ease',
            '&:hover': {
              color: baseTheme.palette.primary.main,
              backgroundColor: `${baseTheme.palette.primary.light}10`,
            },
          },

          '& button[aria-label="Delete"]': {
            color: baseTheme.palette.primary.main,
            width: '36',
            '&:hover': {
              color: baseTheme.palette.common.white,
              backgroundColor: baseTheme.palette.error.main,
            },
          },
        },
      },
    },

    // Card
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.common.white,
          borderRadius: radius.medium,
          boxShadow: 'none',
          border: 'none',
          marginBottom: baseTheme.spacing(2),
        },
      },
    },

    // Table cell
    MuiTableCell: {
      styleOverrides: {
        root: { padding: '12px 16px' },
      },
    },

    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.chip,
          fontWeight: 500,
          backgroundColor: baseTheme.palette.grey[200],
          color: baseTheme.palette.grey[700],
        },
        colorPrimary: {
          backgroundColor: `${baseTheme.palette.primary.main}20`,
          color: baseTheme.palette.primary.dark,
        },
        colorSecondary: {
          backgroundColor: `${baseTheme.palette.secondary.main}20`,
          color: baseTheme.palette.secondary.dark,
        },
        colorSuccess: {
          backgroundColor: `${baseTheme.palette.success.main}20`,
          color: baseTheme.palette.success.dark,
        },
        colorWarning: {
          backgroundColor: `${baseTheme.palette.warning.main}20`,
          color: baseTheme.palette.warning.dark,
        },
        colorError: {
          backgroundColor: `${baseTheme.palette.error.main}20`,
          color: baseTheme.palette.error.dark,
        },
        colorInfo: {
          backgroundColor: `${baseTheme.palette.info.main}20`,
          color: baseTheme.palette.info.dark,
        },
      },
    },

    // Text field
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': { minHeight: '48px' },
        },
      },
    },

    // Select
    MuiSelect: {
      styleOverrides: {
        select: {
          display: 'flex',
          alignItems: 'center',
          minHeight: '22px',
          paddingTop: '8.5px',
          paddingBottom: '8.5px',
        },
        icon: { top: 'calc(50% - 12px)' },
      },
    },

    // Input base
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.MuiInputBase-sizeSmall': { minHeight: '48px' },
          '&:not(.MuiInputBase-sizeSmall)': { minHeight: '56px' },
        },
        input: {
          '&.MuiInputBase-inputSizeSmall': {
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
          },
        },
      },
    },

    // Input label
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-sizeSmall': {
            transform: 'translate(14px, 9px) scale(1)',
          },
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },

    // Form control
    MuiFormControl: {
      styleOverrides: {
        root: { marginTop: 0, marginBottom: 0 },
      },
    },

    // Autocomplete
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          paddingTop: '4px',
          paddingBottom: '4px',
          '&.MuiInputBase-sizeSmall': {
            paddingTop: '2px',
            paddingBottom: '2px',
            '& .MuiAutocomplete-input': {
              paddingTop: '6.5px',
              paddingBottom: '6.5px',
            },
          },
        },
      },
    },

    // Form helper text
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginTop: '3px', marginLeft: '14px', marginRight: '14px' },
      },
    },
  },
}, esES);

export default lightTheme;
